import React, { useState, useEffect, useRef, useMemo } from 'react';

const CTASection = () => {
  const [counters, setCounters] = useState({
    predictors: 0,
    predictions: 0,
    rewards: 0
  });
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const finalStats = useMemo(() => ({
    predictors: 2847,
    predictions: 156000,
    rewards: 89500
  }), []);

  // Intersection Observer 监听组件是否进入视口
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.3, // 当30%的组件可见时触发
        rootMargin: '0px 0px -100px 0px' // 提前一点触发
      }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isVisible]);

  // 当组件可见时开始动画
  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000; // 2秒动画
    const steps = 30; // 减少帧数，降低更新频率
    const stepTime = duration / steps;
    let animationTimer = null;
    let startTimer = null;

    const incrementCounters = () => {
      let currentStep = 0;
      
      animationTimer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        
        setCounters(prev => {
          const newCounters = {
            predictors: Math.floor(finalStats.predictors * progress),
            predictions: Math.floor(finalStats.predictions * progress),
            rewards: Math.floor(finalStats.rewards * progress)
          };
          
          // 只有当值真正改变时才更新状态
          if (prev.predictors !== newCounters.predictors || 
              prev.predictions !== newCounters.predictions || 
              prev.rewards !== newCounters.rewards) {
            return newCounters;
          }
          return prev;
        });

        if (currentStep >= steps) {
          clearInterval(animationTimer);
          setCounters(finalStats);
        }
      }, stepTime);
    };

    // 0.5秒延迟启动动画
    startTimer = setTimeout(incrementCounters, 500);
    
    return () => {
      if (startTimer) clearTimeout(startTimer);
      if (animationTimer) clearInterval(animationTimer);
    };
  }, [isVisible, finalStats]); // 依赖isVisible和finalStats

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const handleLaunchApp = () => {
    window.open('https://app.cricket-ai.xyz/', '_blank');
  };

  return (
    <section 
      ref={sectionRef} 
      className="relative overflow-hidden bg-gray-900 min-h-screen flex flex-col"
      style={{
        background: 'linear-gradient(135deg, #0a0e1a 0%, #1a1f2e 100%) !important'
      }}
    >
      {/* 统计数据部分 - 40% */}
      <div className="flex-none border-b border-white/10" style={{ height: '45vh' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-full flex flex-col justify-center">
          <div className="text-center mb-16">
            <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent mb-4">
              Cricket AI in Numbers
            </div>
            <div className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
              Join thousands of predictors earning real rewards
            </div>
          </div>
          
          <div className="flex flex-row justify-center items-center gap-4 sm:gap-8 md:gap-16 lg:gap-20">
            <div className="text-center relative flex-1">
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-1 sm:mb-2">
                {formatNumber(counters.predictors)}
              </div>
              <div className="text-sm md:text-base lg:text-lg text-gray-400 font-medium leading-tight max-w-20 sm:max-w-24 mx-auto">Active Predictors</div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 sm:w-16 md:w-20 h-12 sm:h-16 md:h-20 border-2 border-emerald-400/30 rounded-full animate-pulse"></div>
            </div>
            
            <div className="w-px h-8 sm:h-12 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
            
            <div className="text-center relative flex-1">
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-1 sm:mb-2">
                {formatNumber(counters.predictions)}+
              </div>
              <div className="text-sm md:text-base lg:text-lg text-gray-400 font-medium leading-tight max-w-20 sm:max-w-24 mx-auto">Predictions Made</div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 sm:w-16 md:w-20 h-12 sm:h-16 md:h-20 border-2 border-emerald-400/30 rounded-full animate-pulse"></div>
            </div>
            
            <div className="w-px h-8 sm:h-12 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
            
            <div className="text-center relative flex-1">
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-1 sm:mb-2">
                ${formatNumber(counters.rewards)}
              </div>
              <div className="text-sm md:text-base lg:text-lg text-gray-400 font-medium leading-tight max-w-20 sm:max-w-24 mx-auto">CRICKET Rewards</div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 sm:w-16 md:w-20 h-12 sm:h-16 md:h-20 border-2 border-emerald-400/30 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA内容部分 - 40% */}
      <div className="flex-none" style={{ height: '35vh' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-full flex flex-col justify-center">
          <div className="text-center">
            <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-600 mb-6">
              Ready to start <div className="bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent font-extrabold inline">predicting?</div>
            </div>
            <button 
              className="group inline-flex items-center gap-3 bg-gradient-to-r from-emerald-400 to-emerald-600 text-gray-900 font-bold px-6 sm:px-8 md:px-12 py-3 sm:py-4 md:py-5 rounded-xl text-base sm:text-lg md:text-xl hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-1 transition-all duration-300"
              onClick={handleLaunchApp}
            >
              Launch App
              <div className="text-lg sm:text-xl group-hover:translate-x-1 transition-transform duration-300">→</div>
            </button>
          </div>
        </div>
      </div>
      
      {/* Footer - 30% */}
      <footer className="flex-none bg-gray-950 border-t border-white/10" style={{ height: '20vh' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-full flex flex-col justify-center py-4">
          <div className="flex flex-col items-center text-center space-y-4">
            <div >
              <div className='flex justify-between items-center py-3 sm:py-4'>
              <img 
                src="/cricket32x32.png" 
                alt="Cricket AI Logo" 
                className="w-6 h-6 sm:w-8 sm:h-8 mr-3"
              />
              <div className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-emerald-400 to-blue-500 bg-clip-text text-transparent">
                Cricket AI
              </div>
              </div>
              <div className="text-gray-500 text-xs sm:text-sm mt-1">Predict. Earn. Repeat.</div>
            </div>
            
            <div className="flex flex-row gap-8 text-xs sm:text-sm md:text-base">
              <div>
                <div className="text-white font-semibold mb-2">Product</div>
                <div className="space-y-1">
                  <a href="#features" className="block text-gray-400 hover:text-emerald-400 transition-colors">Features</a>
                  <a href="#how-it-works" className="block text-gray-400 hover:text-emerald-400 transition-colors">How It Works</a>
                </div>
              </div>
              
              <div>
                <div className="text-white font-semibold mb-2">Community</div>
                <div className="space-y-1">
                  <a href="https://twitter.com/plypto" target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-emerald-400 transition-colors">Twitter</a>
                  <a href="https://discord.gg/plypto" target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-emerald-400 transition-colors">Discord</a>
                </div>
              </div>
            </div>
            
            {/* <div className="border-t border-white/10 pt-3 w-full">
              <div className="text-xs text-gray-500 mb-2">&copy; 2025 Plypto. All rights reserved.</div>
              <div className="flex justify-center gap-4 text-xs">
                <a href="/terms" className="text-gray-500 hover:text-gray-400 transition-colors">Terms</a>
                <a href="/privacy" className="text-gray-500 hover:text-gray-400 transition-colors">Privacy</a>
              </div>
            </div> */}
          </div>
        </div>
      </footer>
    </section>
  );
};

export default CTASection;
